import { ArrowLeftIcon, HourglassMediumIcon, InfoIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useSearchSession } from '@entities/search-session';
import { isBuilding, isLive, useComparison, useSelection } from '@entities/selection';
import { KarlDialogue } from '@features/chat';
import { NoRecommendation, RecommendationBanner } from '@features/conclusion';
import {
  ExhaustedState,
  SelectionCard,
  SelectionComparison,
  SelectionHistory,
} from '@features/selection';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { Button, ButtonLink, KarlIntro, NextStepAction, Skeleton, StateBlock } from '@shared/ui';

import styles from './Top4Page.module.css';

/**
 * Стадии, на которых шаг 6 уже имеет содержимое (§33.2).
 *
 * `offer_review` — финальное предложение получено, решение за пользователем;
 * `conclusion_ready` — заказ дошёл до итогов без торга. На остальных стадиях
 * заключения не существует, и ссылка на него вела бы в пустой экран.
 */
const CONCLUSION_STAGES: readonly string[] = ['offer_review', 'conclusion_ready'];

/**
 * Стадии, на которых проверка по четырём ещё идёт (§36.6). `comparison_ready`
 * и `deep_check` — стадии прежней двухшаговой проверки, у заказов, застигнутых
 * правкой, они означают то же самое.
 */
const CHECK_STAGES: readonly string[] = ['checking', 'comparison_ready', 'deep_check'];

/**
 * T-01 «Ihre 4 besten Autos» — экран четвёрки (FE-220, FE-222).
 *
 * **SoT:** MVP_PRD §34.3–34.6, §38.1; референс `screen4`.
 *
 * Экран собирает и ничего не решает: состав четвёрки, обоснование каждого
 * автомобиля, таблицу сравнения и два действия на карточке. Отбор, замену и
 * подсветку лучшего значения считает backend — фронт их не воспроизводит.
 *
 * Четыре правила экрана:
 *
 * * **четыре карточки равны по весу** (DESIGN_SYSTEM §7). Рекомендованный
 *   вариант помечен и стоит первым, но не крупнее остальных: вёрстка не должна
 *   подсказывать выбор, который делает пользователь;
 * * **выбор не рекомендованного варианта не блокируется**, предупреждение
 *   показывается один раз (MVP_PRD §38.1);
 * * **выбор один и окончателен** (§34.4, решение заказчика от 09.09.2026):
 *   выбрать автомобиль можно, как только Karl закончил вопросы по нему, и в
 *   этот момент остальные три выбывают. Необратимые действия — замена и
 *   выбор — в два шага и без оптимистичного UI; снятия выбора нет;
 * * **исчерпание — штатный исход**, а не ошибка: отдельный экран с разбором по
 *   каждому проверенному автомобилю (§34.6).
 *
 * Собственного контейнера у экрана нет: ширину задаёт оболочка `/app/*`, и
 * второй контейнер поверх неё отнимал бы у четырёх карточек ещё два поля.
 * Контекстного сайдбара тоже нет — по той же причине.
 *
 * **Красная кнопка на экране одна — и только пока есть главное действие**
 * (DESIGN_SYSTEM §2.2, §5.1): до подтверждения четвёрки это «Karl
 * beauftragen». Действия на карточках — синий контур («Выбрать») и `ghost`
 * («Не подходит»); после подтверждения общего действия у шага нет, и ссылки
 * дальше по воронке — вторичные. Вводный абзац под заголовком — реплика Karl
 * (`KarlIntro`, §7.2).
 *
 * **Рекомендация Karl стоит над карточками** (FE-250, MVP_PRD §38.1): она
 * считается детерминированным движком и приходит вместе со сравнением. До
 * завершения контрольной проверки её нет вовсе, и блок не рисуется — сравнение
 * четырёх полезно само по себе. Исход «не рекомендуем ни один» показывается
 * полноценным экраном с выходами, а не пустым местом (§38.4).
 */
export function Top4Page() {
  const { t } = useTranslation();
  const { searchId = '' } = useParams();

  const sessionQuery = useSearchSession(searchId || null);
  const selectionQuery = useSelection(searchId || null);
  const comparisonQuery = useComparison(searchId || null);

  const session = sessionQuery.data ?? null;
  const selection = selectionQuery.data ?? null;

  // Контекстного сайдбара на этом экране нет намеренно: четыре карточки — это
  // и есть содержимое шага, и каждый отнятый у них пиксель уходит в обрезанные
  // подписи. Критерии заказа правятся из диалога с Karl, ссылка на него — в
  // рельсе слева.
  useAppShell({
    stage: session?.session.stage ?? null,
    paid: session?.session.paid ?? false,
    karl: {
      // Диалог живёт в рельсе: уточнить запрос можно прямо отсюда, не уходя с
      // четвёрки. Само уточнение четвёрку **не трогает** — пересчёт запускает
      // кнопка в рельсе, и до неё карточки остаются рабочими (§35.4).
      extra: <KarlDialogue searchId={searchId} />,
    },
    // Шаг 5 трекера — переписка выбранного автомобиля: после выбора она уже
    // есть в составе, отдельного запроса ради неё нет.
    negotiationId:
      selection?.slots.find((slot) => slot.status === 'selected')?.negotiation_id ?? null,
  });

  // --- Загрузка и отказы ----------------------------------------------------

  if (selectionQuery.isPending) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton height="2.5rem" width="45%" />
        <div className={styles.grid}>
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} height="26rem" radius="var(--radius-card)" />
          ))}
        </div>
      </div>
    );
  }

  if (selectionQuery.isError) {
    const error = selectionQuery.error;
    const unpaid = error instanceof ApiError && error.status === 402;
    return (
      <div className={styles.page}>
        <StateBlock
          tone={unpaid ? 'neutral' : 'error'}
          title={unpaid ? t('selection.locked.title') : t('selection.error.title')}
          description={unpaid ? t('selection.locked.text') : t('selection.error.text')}
          action={
            unpaid ? (
              <ButtonLink to={ROUTES.searchPayment(searchId)}>
                {t('selection.locked.action')}
              </ButtonLink>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  void selectionQuery.refetch();
                }}
              >
                {t('error.retry')}
              </Button>
            )
          }
        />
      </div>
    );
  }

  if (selection === null) return null;

  const slots = selection.slots.filter(isLive);
  const currency = slots[0]?.vehicle.price.currency ?? 'EUR';

  const comparison = comparisonQuery.data ?? null;
  const recommendation = comparison?.recommendation ?? null;
  const noRecommendationReason = comparison?.no_recommendation_reason ?? null;
  const recommendedConclusion = comparison?.conclusions.find((item) => item.is_recommended) ?? null;
  const recommendedVehicle = slots.find(
    (slot) => slot.vehicle.vehicle_id === recommendation?.vehicle_id,
  );
  const recommendedLabel =
    recommendedVehicle === undefined
      ? ''
      : `${recommendedVehicle.vehicle.make} ${recommendedVehicle.vehicle.model}`;

  // --- Прогон упал -----------------------------------------------------------

  // Перестройка подбора может не досчитаться. Молчать об этом нельзя: экран
  // «Karl собирает четвёрку» крутился бы вечно, а четвёрки не будет.
  if (selection.stage === 'analysis_failed') {
    return (
      <div className={styles.page}>
        <StateBlock
          tone="error"
          title={t('selection.runFailed.title')}
          description={t('selection.runFailed.text')}
          action={
            <ButtonLink to={ROUTES.searchResults(searchId)} variant="secondary">
              {t('selection.runFailed.action')}
            </ButtonLink>
          }
        />
      </div>
    );
  }

  // --- Отбор ещё идёт --------------------------------------------------------

  // Пустой экран на этих стадиях — ожидание, а не исход: состав опрашивается
  // и заполнится сам. Стадия различает два разных ожидания, и склеивать их в
  // одну подпись нельзя — «пересчитываем каталог» и «собираем четвёрку» это
  // разные по длительности вещи.
  //
  // `analyzing` проверяется **отдельно от пустоты**: слоты прошлого прогона
  // освобождаются только в момент, когда новый досчитан (§34.13), и до тех пор
  // они физически на месте. Показывать их вместе с живыми «Выбрать этот
  // автомобиль» и «Не подходит» значило бы предлагать работу по четвёрке,
  // которой через несколько секунд не станет.
  const rebuilding = selection.stage === 'analyzing';
  if (rebuilding || (slots.length === 0 && isBuilding(selection))) {
    return (
      <div className={styles.page}>
        <StateBlock
          title={rebuilding ? t('selection.rebuilding.title') : t('selection.building.title')}
          description={rebuilding ? t('selection.rebuilding.text') : t('selection.building.text')}
          action={
            rebuilding ? (
              <ButtonLink to={ROUTES.searchResults(searchId)} variant="secondary">
                {t('selection.rebuilding.action')}
              </ButtonLink>
            ) : undefined
          }
        />
      </div>
    );
  }

  // --- Кандидаты закончились (§34.6) ----------------------------------------

  if (selection.exhausted) {
    return (
      <div className={styles.page}>
        <Link to={ROUTES.app} className={styles.back}>
          <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
          {t('searches.backToList')}
        </Link>
        <ExhaustedState selection={selection} />
        <SelectionHistory history={selection.history} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Link to={ROUTES.app} className={styles.back}>
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {t('searches.backToList')}
      </Link>

      <header className={styles.header}>
        <h1 className={styles.title}>
          {/* Акцент в заголовке — разрешённый приём (DESIGN_SYSTEM §3.3):
              один цветной фрагмент, внутри /app/* он синий. */}
          {/* Число согласует все три части, а не только акцент: иначе при одном
              автомобиле выходило «Ihre beste Autos» / «Ваши 1 лучших автомобиля». */}
          {t('selection.title.before', { count: slots.length })}{' '}
          <span className={styles.accent}>
            {t('selection.title.accent', { count: slots.length })}
          </span>{' '}
          {t('selection.title.after', { count: slots.length })}
        </h1>
        {/* Вводный абзац — реплика Karl (DESIGN_SYSTEM §5.4, §7.2): текст
            фиксированный, из локалей, LLM его не пишет. */}
        <KarlIntro>{t('selection.subtitle')}</KarlIntro>
      </header>

      {/* Счётчик проверенных автомобилей: числа приходят с сервера (§34.13). */}
      <p className={styles.counter}>
        {t('selection.checkedCounter', {
          count: selection.checked_vehicle_count,
          max: selection.max_checked_vehicles,
        })}
      </p>

      {/* Подходящих меньше четырёх — честное состояние, а не пустые слоты.
          После заморозки причина другая и названа отдельно: автомобиль выбыл
          или отклонён, и замены **не будет** (§34.5). Обещать её здесь общей
          формулировкой значило бы соврать. */}
      {slots.length < selection.max_size && !selection.top_up_pending ? (
        <p className={styles.notice} role="note">
          <InfoIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          {selection.frozen
            ? t('selection.frozenFewer', { count: slots.length, max: selection.max_size })
            : t('selection.fewerThanMax', { count: slots.length, max: selection.max_size })}
        </p>
      ) : null}

      {/* Иконка ожидания — песочные часы, а не знак спиннера: круглый
          спиннер запрещён (DESIGN_SYSTEM §5.9), и неподвижный обещал бы
          движение, которого нет. */}
      {selection.top_up_pending ? (
        <p className={styles.notice} role="status">
          <HourglassMediumIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          {t('selection.topUpPending')}
        </p>
      ) : null}

      {/* Состояние проверки. Она идёт сама (§36.6): Karl дописывает
          автохаусам, пока по каждому пункту нет ответа или отказа, а выбрать
          автомобиль можно, как только закончилась проверка по нему. Числа —
          из данных, не литералы. Стоит **над** карточками: оно объясняет,
          почему у одних есть «Выбрать», а у других — нет. */}
      {CHECK_STAGES.includes(selection.stage) ? (
        <p className={styles.notice} role="status">
          <HourglassMediumIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          {t('selection.checking', {
            done: slots.filter((slot) => slot.check_completed_at !== null).length,
            total: slots.length,
          })}
        </p>
      ) : null}

      {selection.stage === 'deep_check_ready' ? (
        <p className={styles.notice} role="status">
          <InfoIcon size={16} aria-hidden="true" className={styles.noticeIcon} />
          {t('selection.checkReady')}
        </p>
      ) : null}

      {/* Рекомендация Karl — ранжирование с обоснованием, а не вердикт (§38.1).
          Карточки от неё не переупорядочиваются и не блокируются: выбор
          остаётся за пользователем. */}
      {recommendation === null ? null : (
        <RecommendationBanner
          recommendation={recommendation}
          conclusion={recommendedConclusion}
          weights={comparison?.weights ?? {}}
          vehicleLabel={recommendedLabel}
          algorithmVersion={
            recommendedConclusion?.algorithm_version ??
            comparison?.conclusions[0]?.algorithm_version ??
            ''
          }
        />
      )}

      {/* «Не рекомендуем ни один» — штатный исход и полноценный путь (§38.4). */}
      {noRecommendationReason === null ? null : (
        <NoRecommendation
          reason={noRecommendationReason}
          searchId={searchId}
          canReplace={!selection.frozen}
        />
      )}

      <ul className={styles.grid}>
        {slots.map((slot, index) => (
          <li key={slot.selection_id} className={styles.gridItem}>
            <SelectionCard
              slot={slot}
              searchId={searchId}
              index={index}
              selection={selection}
              // Рекомендация помечается бейджем над карточкой, а не размером и
              // не местом: карточки не переупорядочиваются (§38.1).
              recommended={
                recommendation !== null && slot.vehicle.vehicle_id === recommendation.vehicle_id
              }
            />
          </li>
        ))}
      </ul>

      {/* Общее действие шага 4 — и оно меняется вместе со стадией (§34.4).
          До подтверждения четвёрки: «эти четыре подходят, проверяй» — главное
          действие экрана и единственная красная кнопка. После: ссылки дальше
          по воронке. Это переход, а не действие на этом экране, поэтому они
          вторичные: главное здесь теперь — выбор на карточке. */}
      <div className={styles.outreach}>
        {selection.outreach.accepted_at === null ? (
          <NextStepAction
            to={ROUTES.searchOutreach(searchId)}
            dockLabel={t('selection.outreachCtaShort')}
          >
            {t('selection.outreachCta')}
          </NextStepAction>
        ) : CONCLUSION_STAGES.includes(selection.stage) ? (
          // Шаг 6 существует, только когда торг дошёл до финального
          // предложения: ссылка на пустое заключение вела бы в ожидание,
          // выданное за результат (§38.2).
          <ButtonLink to={ROUTES.searchConclusion(searchId)} variant="secondary">
            {t('selection.conclusionCta')}
          </ButtonLink>
        ) : (
          <ButtonLink to={ROUTES.negotiations} variant="secondary">
            {t('selection.outreachDone')}
          </ButtonLink>
        )}
      </div>

      {comparison === null ? null : (
        <SelectionComparison comparison={comparison} currency={currency} />
      )}

      <SelectionHistory history={selection.history} />
    </div>
  );
}
